import { Injectable } from '@angular/core'
import { Actions, Effect, ofType } from '@ngrx/effects'
import { catchError, concatMap, map, switchMap, tap } from 'rxjs/operators'
import { of } from 'rxjs'
import { ChargeService } from './service'
import {
  AddCharge,
  AddChargeComplete,
  DeleteCharge,
  DeleteChargeComplete,
  DeleteCharges,
  DeleteChargesComplete,
  LoadCharges,
  LoadChargesByIds,
  LoadChargesByIdsComplete,
  LoadChargesComplete,
  UpdateCharge,
  UpdateChargeComplete,
} from './actions'
import { ActionFailed, Toaster } from '@tokeet-frontend/tv3-platform'

@Injectable()
export class ChargeEffects {
  @Effect()
  loadCharges$ = this.actions$.pipe(
    ofType(LoadCharges),
    switchMap(() =>
      this.charges.all().pipe(
        map((charges) => LoadChargesComplete({ charges })),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  loadChargesByIds$ = this.actions$.pipe(
    ofType(LoadChargesByIds),
    switchMap(({ ids }) =>
      this.charges.getByIds(ids).pipe(
        map((charges) => LoadChargesByIdsComplete({ charges })),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  addCharge$ = this.actions$.pipe(
    ofType(AddCharge),
    switchMap(({ payload }) =>
      this.charges.create(payload).pipe(
        map((charge) => AddChargeComplete({ charge })),

        tap(() => this.toast.success($localize`Charge created successfully.`)),
        catchError((error) => {
          if (!error.error?.message) {
            // NOTE: TV4-2333 - this is a workaround for what should be a backend fix
            error.error.message = $localize`Your card was declined.`
          }
          return of(ActionFailed({ error }))
        })
      )
    )
  )

  @Effect()
  updateCharge$ = this.actions$.pipe(
    ofType(UpdateCharge),
    switchMap(({ payload, chargeId }) =>
      this.charges.update(chargeId, payload).pipe(
        map((charge) => UpdateChargeComplete({ update: { id: chargeId, changes: charge } })),

        tap(() => this.toast.success($localize`Charge updated successfully.`)),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  deleteCharge$ = this.actions$.pipe(
    ofType(DeleteCharge),
    switchMap(({ id }) =>
      this.charges.delete(id).pipe(
        map((charge) => DeleteChargeComplete({ id: charge.pkey })),
        tap(() => this.toast.success($localize`Charge deleted successfully.`)),

        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  deleteCharges$ = this.actions$.pipe(
    ofType(DeleteCharges),
    concatMap(({ ids }) =>
      this.charges.deleteCharges(ids).pipe(
        map(() => DeleteChargesComplete({ ids })),
        tap(() => this.toast.success($localize`Charges deleted successfully.`)),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  constructor(private actions$: Actions, private charges: ChargeService, private toast: Toaster) {}
}
